import { useState } from 'react'
import { useAddMonitorTargetsMutation } from '../../api/client'
import Button from '../Button'
import { toastError, toastSuccess, toastWarning } from '../Notification'
import { PlatformConfig } from './platforms'
import { splitTargets } from './MonitorScheduleForm'

type Props = {
    monitorTag: string
    platform: PlatformConfig
    targets: string[]
    onChanged: () => void
}

/**
 * The posts a monitor watches, and a way to add more.
 *
 * Targets live in the work's params, so adding used to mean stopping the
 * monitor and creating another one — losing its run history for the sake of
 * one URL.
 */
function MonitorTargets({ monitorTag, platform, targets, onChanged }: Props) {
    const [adding, setAdding] = useState(false)
    const [text, setText] = useState('')

    const [addTargets, { loading }] = useAddMonitorTargetsMutation({
        onError: (error) => toastError(error.message)
    })

    const submit = async () => {
        const parsed = splitTargets(text)
        if (parsed.length === 0) {
            toastError(`Nothing to add — one ${platform.targetsLabel.toLowerCase()} per line.`)
            return
        }
        const result = await addTargets({ variables: { monitorTag, targets: parsed } })
        const outcome = result.data?.addMonitorTargets
        if (!outcome) return

        // The mutation reports more than success or failure, which is what
        // `changed` is for: "already watches all of those" and "a run is in
        // flight, try again" both left the list alone, and showing them as a
        // green tick told people their posts had been added when they had
        // not. Nothing-happened-but-nothing-broke is a warning; a request
        // against work that has ended or vanished is an error.
        if (outcome.changed) {
            toastSuccess(outcome.message)
            setText('')
            setAdding(false)
            onChanged()
            return
        }
        if (outcome.outcome === 'AlreadyPresent' || outcome.outcome === 'Busy') {
            toastWarning(outcome.message)
            return
        }
        toastError(outcome.message)
    }

    return (
        <div>
            <p className="text-sm text-ink-700">
                Watching {targets.length} {targets.length === 1 ? 'post' : 'posts'}
            </p>
            {targets.length > 0 ? (
                <ul className="mt-1 max-h-32 overflow-y-auto list-none pl-0 space-y-0.5">
                    {targets.map((target) => (
                        <li key={target} className="text-xs text-ink-500 font-mono break-all">
                            {target}
                        </li>
                    ))}
                </ul>
            ) : null}

            {adding ? (
                <div className="mt-2 space-y-2">
                    <label className="block text-sm text-ink-700" htmlFor="add-targets">
                        Add {platform.targetsLabel.toLowerCase()}
                    </label>
                    <textarea
                        id="add-targets"
                        rows={3}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="block w-full border border-ink-300 rounded-md shadow-sm text-sm focus:ring-arwen-500 focus:border-arwen-500"
                    />
                    <p className="text-xs text-ink-500">{platform.targetsHint}</p>
                    <div className="flex items-center gap-2">
                        <Button
                            primary={true}
                            text="Add"
                            size="small"
                            type="button"
                            loading={loading}
                            disabled={loading}
                            onClick={submit}
                        />
                        <Button
                            primary={false}
                            text="Cancel"
                            size="small"
                            type="button"
                            onClick={() => {
                                setText('')
                                setAdding(false)
                            }}
                        />
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setAdding(true)}
                    className="mt-1 text-sm text-arwen-600 hover:text-arwen-700"
                >
                    Add posts…
                </button>
            )}
        </div>
    )
}

export default MonitorTargets
